import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import Container from "../../components/shared/Container";
import UserSidebar from "../shared/UserSidebar";
import FrontFooter from "../../shared/FrontFooter";
import FrontHeader from "../../shared/FrontHeader";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { setLanguage } from "../../redux/slice/languageSlice";
import { useDispatch } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { eventDetails } from "../../services/eventService";
import { langConfig } from "../../config/LangConfig";
import useFindRoute from "../../hooks/useFindRoute";
import {
  changeOrganization,
  organizationDetails,
} from "../../services/organizationService";
import { toast } from "react-toastify";
import { referVerificationDetails } from "../../services/ticketService";
import LoadingFallback from "../../components/elements/LoadingFallback";

const FrontUserLayout = ({}) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const { matchedRoute } = useFindRoute();
  const navigate = useNavigate();
  const { lang: paramsLang } = useParams();
  const { lang } = useSelector((state) => state?.langProvider);
  const { eventId, orgId } = useParams();

  useEffect(() => {
    if (langConfig?.includes(paramsLang)) {
      if (paramsLang !== lang) {
        dispatch(setLanguage(paramsLang));
        localStorage.setItem("lang", paramsLang);
      }
    } else {
      const save_lang = localStorage.getItem("lang");
      let uri_sagement = location?.pathname?.split("/");
      uri_sagement[1] = save_lang || "en";
      navigate(uri_sagement.join("/"));
    }
  }, [paramsLang, lang]);

  const {
    data: eventDetailsData,
    isLoading,
    isFetching,
    error,
    refetch: eventRefetch,
  } = useQuery({
    queryKey: ["event-details", eventId, location?.key],
    queryFn: async () => {
      if (
        eventDetailsData?.routerLocationKey &&
        eventDetailsData?.routerLocationKey !== location?.key
      ) {
        queryClient.removeQueries({
          queryKey: ["event-details"],
          exact: false,
        });
      }
      const res = await eventDetails({ id: eventId, type: "front" });
      return { ...res, routerLocationKey: location?.key };
    },
    enabled: !!eventId,
  });

  function updateEvent(updateData) {
    queryClient.setQueryData(
      ["event-details", eventId, location?.key],
      (state) => {
        const state_data = JSON.parse(JSON.stringify(state || {}));
        if (state_data?.data?._id === updateData?._id) {
          state_data.data = updateData;

          return state_data;
        }
        return state_data;
      }
    );
  }

  const {
    data: isValidOrganization,
    isLoading: isValidOrganizationLoading,
    isFetching: isValidOrganizationFetching,
  } = useQuery({
    queryKey: [
      "check-organization",
      eventDetailsData?.data?.organizationData?._id || orgId,
    ],
    enabled: !!eventDetailsData?.data?.organizationData?._id || !!orgId,
    queryFn: async () => {
      const res = await changeOrganization({
        organization: eventDetailsData?.data?.organizationData?._id || orgId,
        ...(!eventDetailsData?.data?.organizationData?._id && {
          type: "front",
        }),
      });
      if (res?.status === 200) {
        return true;
      } else {
        navigate("/");
        return false;
      }
    },
  });

  const {
    data: organizerDetailsData,
    isLoading: organizerLoading,
    isFetching: organizerFetching,
    refetch: organizationRefetch,
    error: organizationError,
  } = useQuery({
    queryKey: [
      "event-organizer",
      eventDetailsData?.data?.organizationData?._id || orgId,
      isValidOrganization,
    ],
    queryFn: async () => {
      const res = await organizationDetails({
        ...(orgId
          ? { id: orgId, type: "front" }
          : {
              id: eventDetailsData?.data?.organizationData?._id,
            }),
      });

      if (res?.status === 200) {
        const normalizedStyles = Object.keys(
          res?.data?.themeSetting?.styles || {}
        ).reduce((acc, key) => {
          const val = res.data.themeSetting.styles[key];
          acc[key] = val == null ? [] : Array.isArray(val) ? val : [val];
          return acc;
        }, {});

        return {
          ...res,
          data: {
            ...res?.data,
            themeSetting: {
              ...res?.data?.themeSetting,
              styles: normalizedStyles,
            },
          },
        };

        // return {
        //   ...res,
        //   data: {
        //     ...res?.data,
        //     themeSetting: {
        //       ...res?.data?.themeSetting,
        //       styles: Object.assign(
        //         ...Object.keys(res?.data?.themeSetting?.styles || {})?.map(
        //           (key) => ({
        //             [key]: Object.assign(
        //               ...res?.data?.themeSetting?.styles?.[key]?.map(
        //                 (item) => ({
        //                   [item?.key]: item?.value,
        //                 })
        //               ),
        //               {}
        //             ),
        //           })
        //         ),
        //         {}
        //       ),
        //     },
        //   },
        // };
      } else {
        toast.warning(res?.message);
        return {};
      }
    },
    enabled:
      isValidOrganization === true &&
      (!!eventDetailsData?.data?.organizationData?._id || !!orgId),
  });

  const { data: promoterDetails, isLoading: promoterDetailsIsLoading } =
    useQuery({
      enabled: !!queryObject?.referral_code || !!location?.state?.promoterUid,
      queryKey: [
        "promoter-details",
        queryObject?.referral_code || "",
        location?.state?.promoterUid || "",
        location,
      ],
      queryFn: async () => {
        const res = await referVerificationDetails({
          code: queryObject?.referral_code || location?.state?.promoterUid,
          type: "front",
        });
        if (!res?.data?.verified) {
          if (location?.state?.promoterUid) {
            const state = {
              ...location?.state,
              ...(location?.state?.checkoutData && {
                checkoutData: location?.state?.checkoutData?.map((item) => {
                  return {
                    ...item,
                    price: item?.originalPrice,
                    totalPrice: item?.buy_quentity * item?.originalPrice,
                  };
                }),
              }),
            };
            delete state?.promoterUid;

            navigate(location?.pathname, { state: state });
          }
          if (queryObject?.referral_code) {
            navigate(location?.pathname);
          }
        }
        return res;
      },
    });

  function renderOutlet() {
    return (
      <Outlet
        context={
          eventId
            ? {
                event: eventDetailsData,
                eventLoading: isLoading || isFetching,
                selectedLanguage: lang,
                eventRefetch,
                updateEvent,
                eventOrganizer: {
                  organizationRefetch,
                  data: organizerDetailsData?.data,
                  loading:
                    isLoading || isFetching
                      ? true
                      : organizerLoading || organizerFetching,
                },
                eventPromoter: {
                  data: promoterDetails,
                  loading: promoterDetailsIsLoading,
                },
              }
            : {
                organizer: organizerDetailsData,
                loading: organizerLoading || organizerFetching,
                selectedLanguage: lang,
                organizationRefetch,
              }
        }
      />
    );
  }

  return (
    <div className="bg-magnolia ">
      <FrontHeader />
      <Container className={"px-0 items-start"}>
        <div className="w-10 md:w-20 flex-shrink-0 bg-white h-[calc(100vh-6rem)] sticky top-24 z-[40]">
          <UserSidebar />
        </div>
        <div className="w-[calc(100%-40px)] md:w-[calc(100%-80px)] flex-shrink py-6 px-3 md:px-6 pt-0 rounded-lg">
          {isLoading || isValidOrganizationLoading ? (
            <LoadingFallback />
          ) : eventDetailsData?.data?.organizationData?._id || orgId ? (
            isValidOrganization ? (
              renderOutlet()
            ) : null
          ) : (
            renderOutlet()
          )}
        </div>
      </Container>
      <FrontFooter />
    </div>
  );
};

export default FrontUserLayout;
